/**
 * 媒体灯箱的点按关闭。拖过（按下到松开坐标变了）不当成点一下。
 * 必须走 YARL `subscribeSensors`，和 Zoom 同一条总线。
 */
import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';
import {
  CLASS_SLIDE_WRAPPER,
  cleanup,
  type ComponentProps,
  createModule,
  cssClass,
  EVENT_ON_POINTER_CANCEL,
  EVENT_ON_POINTER_DOWN,
  EVENT_ON_POINTER_UP,
  type Plugin,
  useController,
  useEventCallback,
  useLightboxProps,
} from 'yet-another-react-lightbox';

/** Zoom 与单击关闭共用的双击间隔。 */
export const TAP_DOUBLE_CLICK_MS = 200;

const SLIDE_WRAPPER = `.${cssClass(CLASS_SLIDE_WRAPPER)}`;

export function createImagePointerGuardPlugin(): Plugin {
  function GuardModule({ children }: ComponentProps): ReactNode {
    useImagePointerGuard();
    return children;
  }
  return ({ addModule }) => {
    addModule(createModule('image-pointer-guard', GuardModule));
  };
}

export function createDismissPlugin(keep: string): Plugin {
  function DismissModule({ children }: ComponentProps): ReactNode {
    useDismissOnTap(keep);
    return children;
  }
  return ({ addModule }) => {
    addModule(createModule('dismiss', DismissModule));
  };
}

function useImagePointerGuard(): void {
  const { subscribeSensors } = useController();
  const onPointerDown = useEventCallback((event: ReactPointerEvent) => {
    if (event.pointerType === 'mouse' && event.buttons > 1) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const wrapper = target.closest(SLIDE_WRAPPER);
    if (!wrapper || target === wrapper) {
      return;
    }
    event.stopPropagation();
  });

  useEffect(
    () => cleanup(subscribeSensors(EVENT_ON_POINTER_DOWN, onPointerDown)),
    [onPointerDown, subscribeSensors],
  );
}

function useDismissOnTap(keep: string): void {
  const { subscribeSensors } = useController();
  const { close, zoom } = useLightboxProps();
  const delay = zoom?.doubleClickDelay ?? TAP_DOUBLE_CLICK_MS;
  const origin = useRef<{ x: number; y: number } | null>(null);
  const ignoreUp = useRef(false);
  const closeTimer = useRef<number | null>(null);

  const clearTimer = useEventCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  });

  const onPointerDown = useEventCallback((event: ReactPointerEvent) => {
    if (event.button !== 0) {
      return;
    }
    if (closeTimer.current !== null) {
      clearTimer();
      ignoreUp.current = true;
      origin.current = null;
      return;
    }
    origin.current = { x: event.clientX, y: event.clientY };
  });

  const onPointerUp = useEventCallback((event: ReactPointerEvent) => {
    if (event.button !== 0) {
      return;
    }
    if (ignoreUp.current) {
      ignoreUp.current = false;
      origin.current = null;
      return;
    }
    const start = origin.current;
    origin.current = null;
    if (!start) {
      return;
    }
    if (event.clientX !== start.x || event.clientY !== start.y) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element) || (keep && target.closest(keep))) {
      return;
    }
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      close();
    }, delay);
  });

  const onPointerCancel = useEventCallback(() => {
    origin.current = null;
    ignoreUp.current = false;
    clearTimer();
  });

  useEffect(
    () =>
      cleanup(
        subscribeSensors(EVENT_ON_POINTER_DOWN, onPointerDown),
        subscribeSensors(EVENT_ON_POINTER_UP, onPointerUp),
        subscribeSensors(EVENT_ON_POINTER_CANCEL, onPointerCancel),
      ),
    [onPointerCancel, onPointerDown, onPointerUp, subscribeSensors],
  );

  useEffect(() => () => clearTimer(), [clearTimer]);
}
